import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { whatsappApi, templatesApi, ApiError } from '@/lib/api'
import { toast } from '@/hooks/use-toast'
import { showApiError } from '@/lib/errors'

export interface WhatsAppTemplate {
  id: string
  name: string
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'DISABLED' | 'PAUSED'
  category: 'AUTHENTICATION' | 'MARKETING' | 'UTILITY'
  language: string
  components: TemplateComponent[]
  quality_score?: string | { score: number; date: string }
  rejected_reason?: string
  created_time: string
  modified_time: string
  body_text?: string
  whatsapp_template_id?: string
  business_account_id?: string
  created_at?: string
  updated_at?: string
  createdAt?: string
  updatedAt?: string
  businessAccountId?: string
  whatsappTemplateId?: string
}

export interface TemplateComponent {
  type: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS' | 'CAROUSEL'
  text?: string
  headerText?: string
  mediaUrl?: string
  format?: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION'
  example?: {
    header_text?: string[]
    body_text?: string[][]
    body_text_named_params?: Array<{ param_name: string; example: string }>
    header_handle?: string[]
    header_url?: string[]
  }
  buttons?: TemplateButton[]
  cards?: Array<{
    components?: TemplateComponent[]
  }>
}

export interface TemplateButton {
  type: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER'
  text: string
  url?: string
  phone_number?: string
}

export interface TemplateFilters {
  status?: string
  category?: string
  language?: string
  search?: string
}

const parseMeta = (raw: any): any => {
  let val = raw
  for (let i = 0; i < 3; i++) {
    if (typeof val !== 'string') break
    try { val = JSON.parse(val) } catch { break }
  }
  return (typeof val === 'object' && val !== null) ? val : null
}

/** The list rows' shape, shared by the full list and the paged board. */
const mapTemplateRow = (template: any): WhatsAppTemplate => {
  const meta = parseMeta(template.meta_response)
  const created =
    meta?.created_time ||
    template.created_from_meta ||
    template.createdAt ||
    template.created_at ||
    template.created_time ||
    template.created ||
    template.createdDate;
  const updated =
    meta?.last_modified_time ||
    meta?.updated_time ||
    template.updated_from_meta ||
    template.updatedAt ||
    template.updated_at ||
    template.updated_time ||
    template.modifiedAt ||
    template.modified_at ||
    template.modified_time ||
    template.modified ||
    template.modifiedDate;

  return {
    id: template.id,
    whatsapp_template_id: template.whatsapp_template_id,
    name: template.name,
    status: template.status,
    category: template.category,
    language: template.language,
    components: (
      meta?.components ||
      meta?.Components ||
      meta?.template?.components ||
      meta?.template?.Components ||
      template.components ||
      []
    ),
    quality_score: meta?.quality_score,
    rejected_reason: meta?.rejected_reason,
    created_time: created,
    modified_time: updated,
    created_at: created,
    updated_at: updated,
    createdAt: created,
    updatedAt: updated,
    body_text: template.body_text
  };
};

export function useMetaTemplates(
  businessAccountId?: string,
  filters?: TemplateFilters,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['meta-templates', businessAccountId, filters],
    queryFn: async () => {
      if (!businessAccountId) return { templates: [], paging: null }

      const result = await templatesApi.getAll(businessAccountId)

      if (result.error) {
        throw ApiError.fromResult(result)
      }

      const templates = (result.data?.map(mapTemplateRow) || []) as WhatsAppTemplate[]

      return {
        templates,
        paging: null // No pagination info in current API response
      }
    },
    enabled: !!businessAccountId && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Cache for 10 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    refetchOnWindowFocus: false,
    retry: 2
  })
}

/**
 * One server page of the templates board: the server searches, filters and
 * pages, and `total` counts every match. Keyed under ['meta-templates', id] so
 * every existing create / delete / sync invalidation refreshes it too.
 */
export function useTemplatesPaged(
  businessAccountId: string | undefined,
  params: {
    page?: number
    limit?: number
    search?: string
    statuses?: string[]
    categories?: string[]
    createdFrom?: Date
    createdTo?: Date
    /** Keep the previous page on screen while the next one loads (same account only). */
    keepPreviousPage?: boolean
    /** Ask only for `total` - the server returns no rows. For count badges. */
    totalOnly?: boolean
    /** Defaults to true; the query also waits for a business account. */
    enabled?: boolean
  } = {},
) {
  const page = Number.isFinite(Number(params.page)) ? Math.max(1, Number(params.page)) : 1
  const limit = Number.isFinite(Number(params.limit)) ? Math.max(1, Math.min(500, Number(params.limit))) : 10
  const offset = (page - 1) * limit
  const search = params.search?.trim() || ''
  // Sorted so [a,b] and [b,a] share one cache entry.
  const toList = (values?: string[]) =>
    Array.isArray(values) && values.length > 0
      ? [...new Set(values.map((v) => v.toUpperCase()))].sort().join(',')
      : ''
  const statuses = toList(params.statuses)
  const categories = toList(params.categories)
  const createdFrom = params.createdFrom ? params.createdFrom.toISOString() : ''
  const createdTo = params.createdTo ? params.createdTo.toISOString() : ''

  return useQuery({
    queryKey: ['meta-templates', businessAccountId, 'paged', page, limit, search, statuses, categories, createdFrom, createdTo, params.totalOnly ? 'total' : 'rows'],
    queryFn: async () => {
      const result = await templatesApi.getPaged({
        businessAccountId: businessAccountId as string,
        limit,
        offset,
        search: search || undefined,
        statuses: statuses || undefined,
        categories: categories || undefined,
        createdFrom: createdFrom || undefined,
        createdTo: createdTo || undefined,
        totalOnly: params.totalOnly || undefined,
      })

      if (result.error) throw ApiError.fromResult(result)

      const templates = (Array.isArray(result.data) ? result.data.map(mapTemplateRow) : []) as WhatsAppTemplate[]
      const totalRaw = (result as { pagination?: { total?: number } }).pagination?.total
      const total = Number.isFinite(Number(totalRaw)) ? Number(totalRaw) : offset + templates.length

      return { templates, total }
    },
    placeholderData: params.keepPreviousPage
      ? (previous, previousQuery) =>
          previousQuery?.queryKey?.[1] === businessAccountId ? previous : undefined
      : undefined,
    enabled: !!businessAccountId && params.enabled !== false,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

export function useMetaTemplate(templateId?: string, businessAccountId?: string) {
  return useQuery({
    queryKey: ['meta-template', templateId, businessAccountId],
    queryFn: async () => {
      if (!templateId) return null

      const result = await templatesApi.getById(templateId)

      if (result.error) {
        throw ApiError.fromResult(result)
      }

      return result.data
    },
    enabled: !!templateId
  })
}

export function useTemplateLibraries(businessAccountId?: string) {
  return useQuery({
    queryKey: ['template-libraries', businessAccountId],
    queryFn: async () => {
      return { libraries: [] }
    },
    enabled: false // Disabled since not available in local API
  })
}

export function useTemplateTypes() {
  return useQuery({
    queryKey: ['template-types'],
    queryFn: async () => {
      const result = await templatesApi.getTypes()

      if (result.error) {
        throw ApiError.fromResult(result)
      }

      return result.data
    },
    staleTime: 10 * 60 * 1000, // Data stays fresh for 10 minutes
    gcTime: 30 * 60 * 1000, // Cache for 30 minutes
  })
}

export function useCreateMetaTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (templateData: {
      businessAccountId: string
      name: string
      category: string
      language: string
      components: TemplateComponent[]
      templateType?: 'TEXT_ONLY' | 'TEXT_WITH_BUTTONS' | 'IMAGE_HEADER' | 'IMAGE_WITH_BUTTONS' | 'VIDEO_HEADER' | 'VIDEO_WITH_BUTTONS' | 'DOCUMENT_HEADER' | 'OTP_TEMPLATE' | 'OTP_WITH_BUTTONS' | 'CAROUSEL'
      mediaFiles?: Array<{
        url: string
        type: 'IMAGE' | 'VIDEO' | 'DOCUMENT'
        componentIndex: number
      }>
      userId?: string
    }) => {
      const result = await templatesApi.create(templateData)

      if (result.error) {
        throw ApiError.fromResult(result)
      }

      return result.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['meta-templates', variables.businessAccountId] })
      // A success can mean the backend just re-checked and cleared a lost
      // connection — refetch so the red banner doesn't linger.
      queryClient.invalidateQueries({ queryKey: ['business-accounts-v2'] })

      toast({ variant: "success",
        title: "Template Created",
        description: "Your template has been submitted for review.",
      })
    },
    onError: (error: any) => {
      // The number just turned out to be disconnected — refetch accounts so the
      // connection banner (with the reason) shows now, not on the next poll.
      const code = typeof error?.code === 'string' ? error.code : ''
      if (code.startsWith('WHATSAPP_DISCONNECTED') || code === 'WHATSAPP_RECONNECTING') {
        queryClient.invalidateQueries({ queryKey: ['business-accounts-v2'] })
      }
      showApiError(error, { fallback: { title: "Failed to Create Template" } })
    }
  })
}

export function useDeleteMetaTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ templateId, businessAccountId }: { templateId: string, businessAccountId: string }) => {
      const result = await templatesApi.delete(templateId)

      if (result.error) {
        throw ApiError.fromResult(result)
      }

      return result.data
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['meta-templates', variables.businessAccountId] })
      toast({ variant: "success",
        title: "Template Deleted",
        description: "Template has been deleted successfully.",
      })
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to Delete Template" } })
    }
  })
}

export function useSendTemplateMessage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (messageData: {
      to: string
      templateName: string
      language?: string
      parameters?: string[]
      businessAccountId: string
    }) => {
      const result = await whatsappApi.sendTemplate({
        to: messageData.to,
        templateName: messageData.templateName,
        language: messageData.language || 'en_US',
        parameters: messageData.parameters,
        businessAccountId: messageData.businessAccountId
      })

      if (result.error) {
        if (result.error.includes('Template not found in Meta API')) {
          throw new Error(`Template "${messageData.templateName}" not found in Meta API. Please check if the template exists in Meta Business Manager.`)
        }

        if (result.error.includes('Template does not have a valid WhatsApp template ID')) {
          throw new Error(`Template "${messageData.templateName}" is not approved in Meta Business Manager. Please approve the template first.`)
        }

        throw ApiError.fromResult(result)
      }

      return result.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
      queryClient.invalidateQueries({ queryKey: ['conversation-messages'] })
      toast({ variant: "success",
        title: "Template Message Sent",
        description: "Your template message has been sent successfully.",
      })
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to Send Template Message" } })
    }
  })
}

export function useImportTemplates() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (businessAccountId: string) => {
      const result = await whatsappApi.importTemplates({ businessAccountId })

      if (result.error) {
        throw ApiError.fromResult(result)
      }

      return result.data
    },
    onSuccess: (data, businessAccountId) => {
      queryClient.invalidateQueries({ queryKey: ['meta-templates', businessAccountId] })
      toast({ variant: "success",
        title: "Templates Imported",
        description: data.message || `Successfully imported ${data.importedCount || 0} templates.`,
      })
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to Import Templates" } })
    }
  })
}

export function useSyncTemplates() {
  return useMutation({
    mutationFn: async (businessAccountId: string) => {
      const result = await templatesApi.syncTemplates({ businessAccountId })

      if (result.error) {
        throw ApiError.fromResult(result)
      }

      return result
    },
    onSuccess: () => {
      toast({ variant: "success",
        title: "Sync Started",
        description: "Template synchronisation started in the background.",
      })
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to Sync Templates" } })
    }
  })
}

export function getTemplateStatusColor(status: string) {
  switch (status) {
    case 'APPROVED':
      return 'text-green-600 bg-green-100'
    case 'PENDING':
      return 'text-yellow-600 bg-yellow-100'
    case 'REJECTED':
      return 'text-red-600 bg-red-100'
    case 'DISABLED':
      return 'text-gray-600 bg-gray-100'
    case 'PAUSED':
      return 'text-orange-600 bg-orange-100'
    default:
      return 'text-gray-600 bg-gray-100'
  }
}

export function getTemplateCategoryColor(category: string) {
  switch (category) {
    case 'AUTHENTICATION':
      return 'text-blue-600 bg-blue-100'
    case 'MARKETING':
      return 'text-purple-600 bg-purple-100'
    case 'UTILITY':
      return 'text-green-600 bg-green-100'
    default:
      return 'text-gray-600 bg-gray-100'
  }
}

export function usePreviewTemplate() {
  return useMutation({
    mutationFn: async ({ templateId, variables }: { templateId: string, variables?: Record<string, string> }) => {
      const result = await templatesApi.previewTemplate(templateId, variables)

      if (result.error) {
        throw ApiError.fromResult(result)
      }

      return result.data
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to Preview Template" } })
    }
  })
}

export function formatTemplatePreview(template: WhatsAppTemplate) {
  const header = template.components.find(c => c.type === 'HEADER')
  const body = template.components.find(c => c.type === 'BODY')
  const footer = template.components.find(c => c.type === 'FOOTER')
  const buttons = template.components.find(c => c.type === 'BUTTONS')

  return {
    header: header?.text || '',
    body: body?.text || '',
    footer: footer?.text || '',
    buttons: buttons?.buttons?.map(b => b.text) || []
  }
}